/**
 * Lite theme — Favorites store.
 * Persists per-device via localStorage. Logged-in users also sync to
 * `app_data` row `khays_lite_user_<uid>` (RLS scoped to that user).
 *
 * The store is identity-aware: call `attachUser(userId)` whenever auth
 * changes. While userId is set, every mutation pushes to cloud; on
 * sign-in, cloud favorites are pulled and merged with local ones.
 */
import { create } from "zustand";
import { supabase } from "@/integrations/supabase/client";
import { getCurrentTenantId } from "@/lib/tenantContext";
import { logCustomerActivity } from "@/data/customerActivity";

const LS_KEY_BASE = "khays_lite_favorites";
function lsKey() {
  const t = getCurrentTenantId();
  return t ? `${LS_KEY_BASE}__t_${t}` : LS_KEY_BASE;
}

export interface LiteFavorite {
  /** Stable identifier — we use device.name */
  id: string;
  addedAt: number;
}

interface LiteFavState {
  items: LiteFavorite[];
  userId: string | null;
  _rev: number;
  isFavorite: (id: string) => boolean;
  toggle: (id: string) => void;
  add: (id: string) => void;
  remove: (id: string) => void;
  clear: () => void;
  attachUser: (userId: string | null) => Promise<void>;
  reload: () => void;
}

function loadLocal(): LiteFavorite[] {
  try {
    const raw = localStorage.getItem(lsKey());
    if (raw) return JSON.parse(raw);
  } catch {}
  return [];
}

function persistLocal(items: LiteFavorite[]) {
  try { localStorage.setItem(lsKey(), JSON.stringify(items)); } catch {}
}

function cloudKey(userId: string) {
  return `khays_lite_user_${userId}`;
}

async function pushCloud(userId: string, items: LiteFavorite[], cart: unknown) {
  try {
    await supabase.from("app_data").upsert(
      { data_key: cloudKey(userId), data_value: { favorites: items, cart } as any },
      { onConflict: "data_key" }
    );
  } catch (e) {
    console.warn("Lite favorites cloud push failed", e);
  }
}

/**
 * Mirror favorites to the dedicated `customer_favorites` table so they're
 * available to the /account dashboard, admin views, and any non-Lite UI.
 * RLS scopes rows to the signed-in customer.
 */
async function mirrorToCustomerFavorites(userId: string, items: LiteFavorite[]) {
  try {
    const tenantId = getCurrentTenantId();
    // Replace the snapshot for this customer+tenant scope (simple + idempotent).
    let del = supabase.from("customer_favorites").delete().eq("customer_user_id", userId);
    del = tenantId ? del.eq("tenant_id", tenantId) : del.is("tenant_id", null);
    await del;
    if (items.length === 0) return;
    await supabase.from("customer_favorites").insert(
      items.map(f => ({
        customer_user_id: userId,
        tenant_id: tenantId,
        device_key: f.id,
        device_label: f.id,
        metadata: { addedAt: f.addedAt } as any,
      }))
    );
  } catch (e) {
    console.warn("customer_favorites mirror failed", e);
  }
}

async function pullCloud(userId: string): Promise<{ favorites: LiteFavorite[]; cart: unknown } | null> {
  try {
    const { data } = await supabase
      .from("app_data")
      .select("data_value")
      .eq("data_key", cloudKey(userId))
      .maybeSingle();
    return (data?.data_value as any) ?? null;
  } catch (e) {
    console.warn("Lite favorites cloud pull failed", e);
    return null;
  }
}

/** Pull favorites from `customer_favorites` (preferred over the legacy blob). */
async function pullCustomerFavorites(userId: string): Promise<LiteFavorite[]> {
  try {
    const tenantId = getCurrentTenantId();
    let q = supabase
      .from("customer_favorites")
      .select("device_key, created_at, metadata")
      .eq("customer_user_id", userId);
    q = tenantId ? q.eq("tenant_id", tenantId) : q.is("tenant_id", null);
    const { data } = await q;
    if (!data) return [];
    return data.map(r => ({
      id: r.device_key,
      addedAt: (r.metadata as any)?.addedAt || new Date(r.created_at).getTime(),
    }));
  } catch (e) {
    console.warn("customer_favorites pull failed", e);
    return [];
  }
}

export const useLiteFavorites = create<LiteFavState>((set, get) => ({
  items: loadLocal(),
  userId: null,
  _rev: 0,

  isFavorite: (id) => get().items.some(f => f.id === id),

  toggle: (id) => {
    const has = get().items.some(f => f.id === id);
    has ? get().remove(id) : get().add(id);
  },

  add: (id) => {
    if (get().items.some(f => f.id === id)) return;
    const next = [...get().items, { id, addedAt: Date.now() }];
    persistLocal(next);
    set({ items: next, _rev: get()._rev + 1 });
    try { void logCustomerActivity("favorite_add", { id }); } catch {}
    const uid = get().userId;
    if (uid) {
      pushCloud(uid, next, getCartSnapshot());
      mirrorToCustomerFavorites(uid, next);
    }
  },

  remove: (id) => {
    const next = get().items.filter(f => f.id !== id);
    persistLocal(next);
    set({ items: next, _rev: get()._rev + 1 });
    try { void logCustomerActivity("favorite_remove", { id }); } catch {}
    const uid = get().userId;
    if (uid) {
      pushCloud(uid, next, getCartSnapshot());
      mirrorToCustomerFavorites(uid, next);
    }
  },

  clear: () => {
    persistLocal([]);
    set({ items: [], _rev: get()._rev + 1 });
    const uid = get().userId;
    if (uid) {
      pushCloud(uid, [], getCartSnapshot());
      mirrorToCustomerFavorites(uid, []);
    }
  },

  attachUser: async (userId) => {
    set({ userId });
    if (!userId) return;
    // Pull from BOTH sources (legacy app_data blob + dedicated table) and
    // merge with whatever is in localStorage. The dedicated table is the
    // canonical source going forward.
    const [cloud, customerRows] = await Promise.all([
      pullCloud(userId),
      pullCustomerFavorites(userId),
    ]);
    const map = new Map<string, LiteFavorite>();
    for (const f of get().items) map.set(f.id, f);
    if (cloud?.favorites) for (const f of cloud.favorites) map.set(f.id, f);
    for (const f of customerRows) map.set(f.id, f);
    const merged = [...map.values()].sort((a, b) => a.addedAt - b.addedAt);
    persistLocal(merged);
    set({ items: merged, _rev: get()._rev + 1 });
    // Mirror back so both stores converge.
    pushCloud(userId, merged, cloud?.cart ?? getCartSnapshot());
    mirrorToCustomerFavorites(userId, merged);
  },

  reload: () => set({ items: loadLocal(), _rev: get()._rev + 1 }),
}));

// Tiny shim so the cart store can hand its current state to favorites' cloud writes
// without creating a circular import. Wired in liteCartStore.
let cartSnapshotProvider: () => unknown = () => null;
export function registerCartSnapshotProvider(fn: () => unknown) {
  cartSnapshotProvider = fn;
}
function getCartSnapshot() {
  try { return cartSnapshotProvider(); } catch { return null; }
}
